import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

window.NWAccordionCustomPreservesScroll = true;

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  get #preserveScrollOnOpen() {
    return this.dataset.preserveScrollOnOpen === 'true' || Boolean(this.closest('[data-preserve-accordion-scroll]'));
  }

  #controller = new AbortController();

  /** @type {number | null} */
  #summaryTopBeforeOpen = null;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal, capture: true });
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;
    const isMenu = this.classList.contains('menu');

    if (isMobile && this.#disableOnMobile) {
      event.preventDefault();
      return;
    }
    if (isDesktop && this.#disableOnDesktop && isMenu) {
      event.preventDefault();
      return;
    }

    // Capture before the native details toggle so scroll preservation starts from a stable layout.
    if (!this.details.open && this.#preserveScrollOnOpen && isMobileBreakpoint()) {
      this.#summaryTopBeforeOpen = this.summary.getBoundingClientRect().top;
    } else {
      this.#summaryTopBeforeOpen = null;
    }

    // User is closing this accordion: set attribute before toggle so closing animation always runs (not only when :focus-within).
    if (this.details.open) {
      this.details.setAttribute('data-close-with-transition', 'true');
      // Force reflow so the browser commits the attribute before the default action (toggle) runs.
      void this.details.offsetHeight;
      const content = this.details.querySelector('.details-content');
      let fallbackId = 0;
      const cleanup = () => {
        this.details.removeAttribute('data-close-with-transition');
        content?.removeEventListener('transitionend', onTransitionEnd);
        content?.removeEventListener('transitioncancel', onTransitionEnd);
        clearTimeout(fallbackId);
      };
      /** @param {TransitionEvent} e */
      const onTransitionEnd = (e) => {
        if (e.propertyName === 'block-size' || e.propertyName === 'opacity') cleanup();
      };
      content?.addEventListener('transitionend', onTransitionEnd);
      content?.addEventListener('transitioncancel', onTransitionEnd);
      fallbackId = setTimeout(cleanup, 600);
    }
  };

  /** Class to close other accordions with this class in the same section (for accordions that are not rows of the same block). */
  static get closeOthersInSectionClass() {
    return 'accordion-close-others-in-section';
  }

  /**
   * When this accordion opens, close any other open accordion in the same group.
   * - On mobile in the footer: close other interactive accordions across its groups.
   * - With class "accordion-close-others-in-section": close others with that class in the same .shopify-section.
   * - Otherwise: close sibling accordion-custom only (same parent, e.g. accordion rows).
   * Uses data-close-with-transition so the closed item uses the same CSS transition as user-driven close.
   */
  #handleToggle = () => {
    if (!this.details.open) return;
    if (this.dataset.closeSiblingsOnOpen === 'false') return;

    const shouldPreserveScroll = this.#preserveScrollOnOpen && isMobileBreakpoint();
    const summaryTop = shouldPreserveScroll
      ? (this.#summaryTopBeforeOpen ?? this.summary.getBoundingClientRect().top)
      : 0;
    this.#summaryTopBeforeOpen = null;

    const { closeOthersInSectionClass } = AccordionCustom;
    const scope =
      this.classList.contains(closeOthersInSectionClass) &&
      this.closest('.shopify-section');

    /* On desktop, only run close-others for menu accordions; other accordions (e.g. rows) keep close-others on mobile only. */
    const isMenu = this.classList.contains('menu--accordion');
    if (isMenu && scope && !isMobileBreakpoint()) return;

    // Footer groups share one mobile accordion scope, regardless of each row's desktop settings.
    const mobileFooter = isMobileBreakpoint() && this.closest('footer');
    const candidates = mobileFooter
      ? mobileFooter.querySelectorAll('accordion-custom:not([data-disable-on-mobile="true"])')
      : scope
      ? /** @type {NodeListOf<AccordionCustom>} */ (
          scope.querySelectorAll(`accordion-custom.${closeOthersInSectionClass}`)
        )
      : /** @type {NodeListOf<AccordionCustom>} */ (
          this.parentElement?.querySelectorAll('accordion-custom') ?? []
        );

    for (const el of candidates) {
      if (el !== this && el.details?.open) {
        el.details.setAttribute('data-close-with-transition', 'true');
        el.details.open = false;
        const content = el.details.querySelector('.details-content');
        /** @param {TransitionEvent} e */
        const onTransitionEnd = (e) => {
          if (e.propertyName === 'block-size') {
            el.details.removeAttribute('data-close-with-transition');
            content?.removeEventListener('transitionend', onTransitionEnd);
          }
        };
        content?.addEventListener('transitionend', onTransitionEnd);
      }
    }

    if (shouldPreserveScroll) this.#preserveSummaryPosition(summaryTop);
  };

  /**
   * Keeps the opened summary visually anchored while nearby accordions collapse.
   * @param {number} initialTop
   */
  #preserveSummaryPosition(initialTop) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const frameCount = prefersReducedMotion ? 2 : 24;
    let frames = 0;

    const scrollElement = document.scrollingElement || document.documentElement;
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;

    const preserve = () => {
      const currentTop = this.summary.getBoundingClientRect().top;
      const delta = currentTop - initialTop;

      if (Math.abs(delta) > 1) {
        document.documentElement.style.scrollBehavior = 'auto';
        scrollElement.scrollTop += delta;
      }

      frames += 1;
      if (frames < frameCount) {
        requestAnimationFrame(preserve);
      } else {
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      }
    };

    preserve();
    requestAnimationFrame(preserve);
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
